import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, LogIn, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !password.trim()) {
      toast.error('Please fill in all fields!');
      return;
    }

    setLoading(true);
    try {
      const user = await login(name.trim(), password);
      toast.success(`Welcome back, ${user.display_name || user.name}!`);
      
      if (!user.onboarding_complete) {
        navigate('/onboarding');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Something went wrong. Try again!';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="/brain-gym-logo.png" 
            alt="Brain Gym Jr. Logo" 
            className="w-32 h-32 mx-auto mb-4 animate-bounce-soft"
          />
          <h1 className="text-4xl md:text-5xl font-black text-slate-900 font-fredoka">
            Brain Gym Jr.
          </h1>
          <p className="text-lg text-slate-600 mt-2">Welcome back, brain champion!</p>
        </div>

        {/* Login Form */}
        <div className="card-bubbly">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-lg font-bold text-slate-700">
                Your Name
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your nickname"
                className="input-friendly"
                data-testid="login-name-input"
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-lg font-bold text-slate-700">
                Secret Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your secret code"
                className="input-friendly"
                data-testid="login-password-input"
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              className="btn-primary w-full flex items-center justify-center gap-2"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Logging in...
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  Let's Go!
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-600">
              New here?{' '}
              <Link
                to="/signup"
                className="text-secondary font-bold hover:underline"
                data-testid="signup-link"
              >
                Create an account!
              </Link>
            </p>
          </div>

          <div className="mt-4 flex justify-center gap-4 text-xs text-slate-400">
            <Link to="/privacy-policy" className="hover:text-slate-600 hover:underline" data-testid="login-privacy-link">Privacy Policy</Link>
            <span>|</span>
            <Link to="/terms" className="hover:text-slate-600 hover:underline" data-testid="login-terms-link">Terms of Service</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
